import React, { useContext, useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  writeBatch,
  orderBy,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { toast } from "react-toastify";
import { db, storage, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import {
  getClientEditMeta,
  postSliderSnapshot,
  postSnapshot,
  userSnapshot,
  websiteSnapshot,
  writeFullAuditLog,
} from "../utils/editAudit";
const DataContext = React.createContext();
export const useData = () => {
  return useContext(DataContext);
};

const DataProvider = ({ children }) => {
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [postsData, setPostsData] = useState([]);
  const [categoriesData, setCategoriesData] = useState(null);
  const [websiteContentData, setWebsiteContentData] = useState(null);
  const [logo, setLogo] = useState(null);

  //---------------------Fetching all data at start---------------------------

  useEffect(() => {
    fetchLogo();
    setTableLoading(false);
    fetchUsers();
    fetchPosts();
    fetchCategories();
    fetchWebsiteContent();
  }, []);

  //-------------------------------Logo---------------------------------------
  const fetchLogo = async () => {
    const storageRef = ref(storage, `assets/logo`);
    const downloadURL = await getDownloadURL(storageRef);
    setLogo(downloadURL);
  };

  // --------------------------Users related----------------------------------
  //Fetch users data from firebase
  const formatDate = (timestamp) => {
    const date = new Date(timestamp.seconds * 1000); // Convert Firestore timestamp to JavaScript Date object
    return date.toLocaleDateString(); // Adjust the date format as needed
  };

  const fetchUsers = async () => {
    try {
      //get data orderd by registration time
      const querySnapshot = await getDocs(collection(db, "users" )  , query("registration_time" , "desc"));
      //sort by registration time
      
      const temp = querySnapshot.docs.sort((a, b) => {
        return b.data().registration_time - a.data().registration_time;
      }).map((doc) => {

        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
          email: data.email,
          registration_time: data.registration_time
            ? formatDate(data.registration_time)
            : "N/A",
          last_edit_ip: data.last_edit_ip,
          last_edit_location: data.last_edit_location,
          last_edit_at: data.last_edit_at,
          last_edit_by: data.last_edit_by,
          last_edit_action: data.last_edit_action,
        };
      });
      setTableData(temp);
    } catch (e) {
      console.log(e);
    }
  };

  // Fetch user to update user name in the firebase
  const fetchAndUpdateName = async (user, updatedUsername) => {
    if (!user?.email || !updatedUsername?.trim()) return;
    setDeleteLoading(true);
    try {
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("email", "==", user.email));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error("User not found");
      }

      const before = userSnapshot(user);
      const after = { ...before, name: updatedUsername.trim() };

      const audit = await getClientEditMeta({
        section: "users",
        action: "update",
        targetId: user.id,
      });
      const stamp = { ...audit };
      delete stamp._geo;

      const updatePromises = querySnapshot.docs.map((docSnapshot) => {
        const userDocRef = doc(usersRef, docSnapshot.id);
        return updateDoc(userDocRef, {
          name: updatedUsername.trim(),
          ...stamp,
        });
      });

      await Promise.all(updatePromises);

      await writeFullAuditLog({
        section: "users",
        action: "update",
        collectionName: "users",
        targetId: user.id || querySnapshot.docs[0].id,
        before,
        after,
        meta: audit,
      });

      toast.success("Successfully updated user!", {
        position: "bottom-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      setDeleteLoading(false);
      fetchUsers();
    } catch (error) {
      toast.error("Error occurred while updating user!", {
        position: "bottom-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      setDeleteLoading(false);
      console.error("Error updating name:", error);
    }
  };

  // Delete user from firebase
  const deleteUserByEmail = async (email, user) => {
    setDeleteLoading(true);

    const deleteUserFunction = httpsCallable(functions, "deleteUser");

    try {
      const before = userSnapshot(user);
      const audit = await getClientEditMeta({
        section: "users",
        action: "delete",
        targetId: user?.id,
      });

      const result = await deleteUserFunction({ email: email });

      if (result.data.error) {
        throw new Error(result.data.error);
      }

      await writeFullAuditLog({
        section: "users",
        action: "delete",
        collectionName: "users",
        targetId: user?.id,
        before,
        after: null,
        meta: audit,
      });

      toast.success("Successfully deleted user!", {
        position: "bottom-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      setDeleteLoading(false);
      fetchUsers();
      fetchPosts();
    } catch (error) {
      toast.error("Error occurred while deleting user!", {
        position: "bottom-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      setDeleteLoading(false);
      console.error("Error deleting user document:", error);
    }
  };

  // --------------------------Posts related--------------------------------
  const addPost = async (newPost) => {
    try {
      const audit = await getClientEditMeta({
        section: "posts",
        action: "create",
      });
      const stamp = { ...audit };
      delete stamp._geo;

      const docRef = await addDoc(collection(db, "posts"), {
        ...newPost,
        ...stamp,
      });

      await writeFullAuditLog({
        section: "posts",
        action: "create",
        collectionName: "posts",
        targetId: docRef.id,
        before: null,
        after: postSnapshot({ ...newPost, id: docRef.id }),
        meta: { ...audit, last_edit_target_id: docRef.id },
        extra: {
          attachment_count: Array.isArray(newPost.attachments)
            ? newPost.attachments.length
            : 0,
          upload_type: newPost.upload_type || null,
        },
      });

      toast.success("Successfully added new post!", {
        position: "bottom-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      setDeleteLoading(false);
      fetchPosts();
    } catch (error) {
      console.error("Error adding post: ", error);
      toast.error("Error occurred while adding post!", {
        position: "bottom-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      setDeleteLoading(false);
      throw error;
    }
  };

  //Fetch posts data from firebase
  const fetchPosts = async () => {
    try {
      const temp = [];
      console.log("fetching posts");
      const postsQuery = query(collection(db, "posts"), orderBy("date_uploaded" , "desc"));
      const querySnapshot = await getDocs(postsQuery);
            querySnapshot.forEach((doc) => {
        console.log(doc.id);
        temp.push({ ...doc.data(), id: doc.id });
      });
      console.log(temp);
      setPostsData(temp);
    } catch (e) {
      console.log(e);
    }
  };

  // Fetch post to update is_pending in the firebase
  const fetchAndUpdatePendingPost = async (post) => {
    if (!post?.id) return;
    setDeleteLoading(true);
    try {
      const postDocRef = doc(db, "posts", post.id);
      const before = postSnapshot(post);
      const after = { ...before, is_pending: false };

      const audit = await getClientEditMeta({
        section: "posts",
        action: "approve",
        targetId: post.id,
      });
      const stamp = { ...audit };
      delete stamp._geo;

      await updateDoc(postDocRef, {
        is_pending: false,
        ...stamp,
      });

      await writeFullAuditLog({
        section: "posts",
        action: "approve",
        collectionName: "posts",
        targetId: post.id,
        before,
        after,
        meta: audit,
      });

      toast.success("Successfully approved post!", {
        position: "bottom-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      setDeleteLoading(false);
      fetchPosts();
    } catch (error) {
      console.error("Error approving post:", error);
      toast.error("Error occurred while approving post!", {
        position: "bottom-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      setDeleteLoading(false);
      fetchPosts();
    }
  };

  const togglePostSlider = async (post) => {
    if (!post?.id) return;
    const promoting = !post.is_slider;
    if (promoting && !post.thumbnail) {
      toast.error("Post needs a thumbnail before promoting to slider!", {
        position: "bottom-left",
        autoClose: 5000,
        theme: "light",
      });
      return;
    }
    setDeleteLoading(true);
    try {
      const postDocRef = doc(db, "posts", post.id);
      let sliderOrder = 0;
      if (promoting) {
        const maxOrder = postsData
          .filter((p) => p.is_slider)
          .reduce((max, p) => Math.max(max, Number(p.slider_order) || 0), 0);
        sliderOrder = maxOrder + 1;
      }

      const before = postSliderSnapshot(post);
      const after = {
        ...before,
        is_slider: promoting,
        slider_order: sliderOrder,
      };

      const audit = await getClientEditMeta({
        section: "slider",
        action: promoting ? "promote" : "demote",
        targetId: post.id,
      });
      const stamp = { ...audit };
      delete stamp._geo;

      await updateDoc(postDocRef, {
        is_slider: promoting,
        slider_order: sliderOrder,
        ...stamp,
      });

      await writeFullAuditLog({
        section: "slider",
        action: promoting ? "promote" : "demote",
        collectionName: "posts",
        targetId: post.id,
        before,
        after,
        meta: audit,
      });

      toast.success(
        promoting ? "Promoted to Slider!" : "Removed from Slider!",
        {
          position: "bottom-left",
          autoClose: 5000,
          theme: "light",
        }
      );
      await fetchPosts();
    } catch (error) {
      console.error("Error toggling slider:", error);
      toast.error("Error updating slider status!", {
        position: "bottom-left",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  // Delete post from firebase
  const deletePostById = async (
    post,
    { refresh = true, silent = false } = {}
  ) => {
    if (!post?.id) return;
    setDeleteLoading(true);
    try {
      const postDocRef = doc(db, "posts", post.id);
      const before = postSnapshot(post);

      const audit = await getClientEditMeta({
        section: "posts",
        action: "delete",
        targetId: post.id,
      });

      await deleteDoc(postDocRef);

      await writeFullAuditLog({
        section: "posts",
        action: "delete",
        collectionName: "posts",
        targetId: post.id,
        before,
        after: null,
        meta: audit,
      });

      if (!silent) {
        toast.success("Successfully deleted post!", {
          position: "bottom-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
      }
      setDeleteLoading(false);
      if (refresh) {
        fetchPosts();
      }
    } catch (error) {
      console.error("Error deleting post:", error);
      if (!silent) {
        toast.error("Error occurred while deleting post!", {
          position: "bottom-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
      }
      setDeleteLoading(false);
      throw error;
    }
  };

  //----------------------Website Content related--------------------------
  //Fetch content data from firebase
  const fetchWebsiteContent = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "admin"));
      if (!querySnapshot.empty) {
        const docSnapshot = querySnapshot.docs[0];
        const websiteContent = { id: docSnapshot.id, ...docSnapshot.data() };
        setWebsiteContentData(websiteContent);
      } else {
        console.log("No documents found in the 'admin' collection.");
      }
    } catch (error) {
      console.log("Error: ", error);
    }
  };

  // Fetch and update content in the firebase
  const fetchAndUpdateWebsiteContent = async (updateData, logoData) => {
    setDeleteLoading(true);
    try {
      if (logoData.file) {
        // Upload the logo image to Firebase Storage
        const storageRef = ref(storage, `assets/logo`);
        await uploadBytes(storageRef, logoData.file)
          .then(() => {
            console.log("Uploaded logo successfully.");
          })
          .catch((e) => {
            console.error("Error uploading logo: ", e);
          });

        // Get the download URL of the uploaded image
        const downloadURL = await getDownloadURL(storageRef);
        setLogo(downloadURL);
      }
      // Get the reference to the document in the "admin" collection
      const websiteContentDocRef = doc(db, "admin", updateData.id);

      const audit = await getClientEditMeta({
        section: "website",
        action: "update",
        targetId: updateData.id,
      });
      const stamp = { ...audit };
      delete stamp._geo;

      const before = websiteSnapshot(websiteContentData);

      await updateDoc(websiteContentDocRef, {
        ...updateData,
        ...stamp,
      })
        .then(async () => {
          await writeFullAuditLog({
            section: "website",
            action: "update",
            collectionName: "admin",
            targetId: updateData.id,
            before,
            after: websiteSnapshot({ ...updateData }),
            meta: audit,
            extra: {
              logo_uploaded: Boolean(logoData?.file),
            },
          });
          toast.success("Successfully updated website content!", {
            position: "bottom-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          setDeleteLoading(false);
          fetchWebsiteContent();
        })
        .catch((err) => {
          toast.error("Error occurred while updating website content!", {
            position: "bottom-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          setDeleteLoading(false);
          fetchWebsiteContent();
          console.error("Error fetching web data: ", err);
        });
    } catch (error) {
      console.error("Error updating website content:", error);
    }
  };

  // --------------------------Categories related--------------------------------
  //Fetch categories data from firebase
  const fetchCategories = async () => {
    try {
      const temp = [];
      const querySnapshot = await getDocs(collection(db, "categories"));
      querySnapshot.forEach((doc) => {
        temp.push({ id: doc.id, ...doc.data() });
      });
      setCategoriesData(temp);
    } catch (e) {
      console.log("Error fetching categories: ", e);
    }
  };

  //  Add a new category to the collection
  const addCategory = async (newCategory) => {
    try {
      await addDoc(collection(db, "categories"), newCategory)
        .then(() => {
          toast.success("Successfully added new category!", {
            position: "bottom-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          setDeleteLoading(false);
          fetchCategories();
        })
        .catch((err) => {
          toast.error("Error occurred while adding category!", {
            position: "bottom-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          setDeleteLoading(false);
          fetchCategories();
          console.error("Error: ", err);
        });
    } catch (error) {
      console.error("Error adding category: ", error);
    }
  };

  // Fetch and update categories in the firebase
  const fetchAndUpdateCategories = async (updateData) => {
    try {
      // Get the reference to the document in the "categories" collection
      const categoryDocRef = doc(db, "categories", updateData.id);

      // Update the document fields with the provided data object
      await updateDoc(categoryDocRef, updateData)
        .then(() => {
          toast.success("Successfully updated the category!", {
            position: "bottom-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          setDeleteLoading(false);
          fetchCategories();
        })
        .catch((err) => {
          toast.error("Error occurred while updating category!", {
            position: "bottom-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          setDeleteLoading(false);
          fetchCategories();
          console.error("Error: ", err);
        });

      // Fetch the updated categories
      fetchCategories();
      console.log("Categories updated successfully!");
    } catch (error) {
      console.error("Error updating categories:", error);
    }
  };

  // Delete category from firebase
  const deleteCategoryById = async (id) => {
    setDeleteLoading(true);
    try {
      // Get the reference to the document in the "categories" collection
      const categoryDocRef = doc(db, "categories", id);

      // Delete the document
      await deleteDoc(categoryDocRef)
        .then(() => {
          toast.success("Successfully deleted the category!", {
            position: "bottom-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          setDeleteLoading(false);
          fetchCategories();
        })
        .catch((err) => {
          toast.error("Error occurred while deleting category!", {
            position: "bottom-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          setDeleteLoading(false);
          fetchCategories();
          console.error("Error: ", err);
        });

      fetchCategories();
      console.log("Category deleted successfully!");
    } catch (error) {
      console.error("Error deleting category:", error);
    }
  };

  const resetCategoriesForOrder = async (updatedData) => {
    // setDeleteLoading(true);
    try {
      // Delete the existing "categories" collection
      const categoriesCollectionRef = collection(db, "categories");
      const categoriesSnapshot = await getDocs(query(categoriesCollectionRef));
      const categoriesExist = !categoriesSnapshot.empty;

      if (categoriesExist) {
        // If the collection exists, delete all existing documents
        const batch = writeBatch(db);
        categoriesSnapshot.forEach((doc) => {
          batch.delete(doc.ref);
        });
        await batch.commit();
      }

      // Create a new collection and add each object as a document
      const batch = writeBatch(db);
      const newCategoriesCollectionRef = collection(db, "categories");
      updatedData.forEach(({ id, ...dataWithoutId }) => {
        const docRef = doc(newCategoriesCollectionRef);
        batch.set(docRef, dataWithoutId);
      });

      await batch.commit().then(() => {
        toast.success("Categories order updated successfully!", {
          position: "bottom-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
        console.log("Categories updated successfully!");
        fetchCategories();
        setDeleteLoading(false);
      });

      // Fetch the updated categories
    } catch (error) {
      toast.error("Error occurred while updating category!", {
        position: "bottom-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      fetchCategories();

      setDeleteLoading(false);
      console.error("Error: ", error);
    }
  };

  const Colors = {
    primaryColor: "#172B6A",
    accentColor: "#005188",
    accentColorLight: "#8FA8C9",
  };

  const logs = [
    { lid: 0, uid: "12", date: Date(), message: "success login to system!" },
    { lid: 1, uid: "12", date: Date(), message: "faliure login to system!" },
    { lid: 2, uid: "12", date: Date(), message: "faliure login to system!" },
    { lid: 3, uid: "12", date: Date(), message: "success login to system!" },
    { lid: 4, uid: "12", date: Date(), message: "success login to system!" },
    { lid: 5, uid: "12", date: Date(), message: "warning login to system!" },
    { lid: 6, uid: "12", date: Date(), message: "warning login to system!" },
    { lid: 7, uid: "12", date: Date(), message: "success login to system!" },
    { lid: 8, uid: "12", date: Date(), message: "fatal login to system!" },
    { lid: 9, uid: "12", date: Date(), message: "fatal login to system!" },
    { lid: 10, uid: "15", date: Date(), message: "success login to system!" },
    { lid: 11, uid: "15", date: Date(), message: "success login to system!" },
    { lid: 12, uid: "15", date: Date(), message: "faliure login to system!" },
    { lid: 13, uid: "15", date: Date(), message: "faliure login to system!" },
    { lid: 14, uid: "15", date: Date(), message: "success login to system!" },
    { lid: 15, uid: "15", date: Date(), message: "success login to system!" },
    { lid: 16, uid: "15", date: Date(), message: "warning login to system!" },
    { lid: 17, uid: "15", date: Date(), message: "warning login to system!" },
  ];

  function siRound(x) {
    if (x < 1e3) return x + "";
    const digits = Math.log10(x) | 0;
    const tier = (digits / 3) | 0;
    let str = (x / 10 ** (tier * 3)).toFixed(2 - (digits % 3));
    // Turn "10.00" into "10.0" and "100.0" into "100"
    str = str.replace(/^(.{3})\..+|^(.{4}).+/, "$1$2");
    if (str.length > 4) {
      ///Add this check
      siRound(str);
    }
    return str + ["", "k", "M", "G", "T"][tier];
  }

  return (
    <DataContext.Provider
      value={{
        logs,
        deleteLoading,
        setDeleteLoading,
        tableData,
        setTableData,
        tableLoading,
        Colors,
        fetchAndUpdateName,
        deleteUserByEmail,
        postsData,
        setPostsData,
        addPost,
        fetchAndUpdatePendingPost,
        togglePostSlider,
        deletePostById,
        fetchPosts,
        categoriesData,
        setCategoriesData,
        addCategory,
        fetchAndUpdateCategories,
        deleteCategoryById,
        resetCategoriesForOrder,
        websiteContentData,
        fetchAndUpdateWebsiteContent,
        logo,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};
export default DataProvider;
